import { HttpErrorResponse } from '@angular/common/http';
import { ErrorMetadata, LogMessage } from '../models/log-message.model';
import { CapturedPayload } from '../transporters/transporter-template.model';
import { LogEvent } from './log-event.model';
import { logLevelValuesMap } from './log-levels-values.map';

/**
 * Default and sample implementation of a TransportTemplate function
 * @example
 * function exampleTransportTemplate({ payload, ...capturedPayload }: CapturedPayload): LogMessage {
 *
 *  const baseMessage: Partial<LogMessage> = {};
 *  let meta: ErrorMetadata = { message: null };
 *
 *  if (typeof payload === 'string') {
 *    // build message from string payload
 *    meta = { message: payload };
 *  } else if ((payload as LogEvent).eventName != null) {
 *    // build message from LogEvent payload
 *    const { eventName, ...rest } = payload as LogEvent;
 *    meta = { message: eventName, ...rest };
 *  } else if (payload instanceof HttpErrorResponse) {
 *    // build message from HttpErrorResponse payload
 *    meta = {
 *        message: payload.statusText,
 *        errorTrace: payload.message,
 *        status: payload.status,
 *        url: payload.url,
 *      };
 *    } else if (payload instanceof Error) {
 *      // build message from Error payload
 *      meta = {
 *       message: payload.message,
 *       errorTrace: payload.stack
 *      };
 *    }
 *    // return aggregation
 *    const log = {
 *      ...baseMessage,
 *      timestamp: capturedPayload.timestamp,
 *      level: logLevelValuesMap[capturedPayload.level],
 *      meta,
 *    };
 *    return log;
 *  }
 * ```
 */
export function defaultTransportTemplate({ payload, ...capturedPayload }: CapturedPayload): LogMessage {
  /*
   * init base & metadata values
   */
  const baseMessage: Partial<LogMessage> = {};
  let meta: ErrorMetadata = { message: null };

  if (typeof payload === 'string') {
    /*
     * Build metadata from string payload
     */
    meta = { message: payload };
  } else if ((payload as LogEvent).eventName != null) {
    /*
     * Build metadata from LogEvent payload
     */
    const { eventName, ...rest } = payload as LogEvent;
    meta = { message: eventName, ...rest };
  } else if (payload instanceof HttpErrorResponse) {
    /*
     * build from HttpError
     */
    meta = {
      message: payload.statusText,
      errorTrace: payload.message,
      status: payload.status,
      url: payload.url,
    };
  } else if (payload instanceof Error) {
    /*
     * build from runtime Error(s)
     */
    meta = { message: payload.message, errorTrace: payload.stack };
  }

  return {
    ...baseMessage,
    timestamp: capturedPayload.timestamp,
    level: logLevelValuesMap[capturedPayload.level],
    meta,
  };
}
